/* eslint-disable @silverhand/fp/no-mutation -- Component-owned refs synchronously fence overlapping async identity operations and discard late results. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import {
  getOrganizationIdentityInfo,
  confirmOrganizationIdentity,
  cancelOrganizationIdentity,
  type OrganizationIdentityInfo,
} from '@/apis/organization-identity';
import SelectField from '@/components/InputFields/SelectField';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import Button from '@/shared/components/Button';

import styles from './index.module.scss';

/** This page belongs to the central authorization interaction, before the relying-party callback. */
const OrganizationIdentity = () => {
  const { t } = useTranslation();
  const redirectTo = useGlobalRedirectTo();
  const [info, setInfo] = useState<OrganizationIdentityInfo>();
  const [selected, setSelected] = useState('');
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const operation = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const result = await getOrganizationIdentityInfo(controller.signal);
        if (!controller.signal.aborted) {
          setInfo(result);
          setSelected(result.organizations[0]?.id ?? '');
          setError(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setInfo(undefined);
          setError(true);
        }
      }
    };
    void load();
    return () => {
      controller.abort();
    };
  }, [retry]);

  const confirm = useCallback(async () => {
    if (!info || !selected || operation.current) {
      return;
    }
    operation.current = true;
    setBusy(true);
    setError(false);
    try {
      const result = await confirmOrganizationIdentity(selected, info.revision);
      await redirectTo(result.redirectTo);
    } catch {
      setError(true);
      setInfo(undefined);
    } finally {
      operation.current = false;
      setBusy(false);
    }
  }, [info, selected, redirectTo]);

  useEffect(() => {
    if (info?.organizations.length === 1) {
      void confirm();
    }
  }, [info, confirm]);

  const cancel = async () => {
    if (operation.current) {
      return;
    }
    operation.current = true;
    setBusy(true);
    try {
      const result = await cancelOrganizationIdentity();
      await redirectTo(result.redirectTo);
    } catch {
      setError(true);
    } finally {
      operation.current = false;
      setBusy(false);
    }
  };

  return (
    <LandingPageLayout title="description.organization_identity_title">
      <div className={styles.content}>
        {info && (
          <p>
            {t('description.organization_identity_description', { name: info.applicationName })}
          </p>
        )}
        {error ? (
          <p role="alert">{t('description.organization_identity_error')}</p>
        ) : !info || info.organizations.length === 1 ? (
          <p role="status">{t('description.organization_identity_loading')}</p>
        ) : (
          <SelectField
            label={t('account_center.departments.organization')}
            value={selected}
            options={info.organizations.map(({ id, name }) => ({ value: id, label: name }))}
            onChange={setSelected}
          />
        )}
        <div className={styles.actions}>
          <Button
            title="action.cancel"
            type="secondary"
            isDisabled={busy}
            onClick={() => {
              void cancel();
            }}
          />
          {error ? (
            <Button
              title="account_center.security.trusted_devices.retry"
              isDisabled={busy}
              onClick={() => {
                setRetry(retry + 1);
              }}
            />
          ) : (
            info &&
            info.organizations.length > 1 && (
              <Button
                title="action.continue"
                isLoading={busy}
                isDisabled={!selected}
                onClick={() => {
                  void confirm();
                }}
              />
            )
          )}
        </div>
      </div>
    </LandingPageLayout>
  );
};

export default OrganizationIdentity;

/* eslint-enable @silverhand/fp/no-mutation */
